import { useState } from 'react';
import { HardOfferData, Tradeline, Recommendation } from 'handlers/applicationData';
import { SelectedTradeline, TradelineNewState } from 'api/ApplicationDataApi';
import { setTradelinesForConsolidationAndCalculateBalance } from 'utils/tradelineUtils';

interface UseDebtConsolidationStateProps {
  data: HardOfferData;
  maxLoanAmount: number;
}

export type SelectedTradelineState = Record<string, SelectedTradeline>;

export const getHardOfferTradelines = (data: HardOfferData) => [...data.payOff, ...data.keepIt];

const applyModificationsToTradelines = (
  originalTradelines: Tradeline[],
  modifications: SelectedTradelineState,
): Tradeline[] =>
  originalTradelines.map((tradeline) => {
    const modification = modifications[tradeline.id];

    if (!modification) return tradeline;

    if (modification.newState === TradelineNewState.Unselected) {
      return {
        ...tradeline,
        selectedForConsolidation: false,
        balanceToConsolidate: 0,
      };
    }

    return {
      ...tradeline,
      selectedForConsolidation: true,
      balanceToConsolidate: modification.tradeline.balanceToConsolidate,
    };
  });

const useDebtConsolidationState = ({ data, maxLoanAmount }: UseDebtConsolidationStateProps) => {
  const [tradelinesModifications, setSelectedTradelines] = useState<SelectedTradelineState>({});

  const originalTradelines = getHardOfferTradelines(data);
  const modifiedTradelines = applyModificationsToTradelines(originalTradelines, tradelinesModifications);
  const [payOff, keepIt] = modifiedTradelines.reduce(
    (acc, t) => {
      if (t.recommendation === Recommendation.PayOff) {
        acc[0].push(t);
      } else if (t.recommendation === Recommendation.KeepIt) {
        acc[1].push(t);
      }
      return acc;
    },
    [[], []] as [Tradeline[], Tradeline[]],
  );

  const { cantConsolidate } = data;

  const toggleTradelineConsolidation = ({ newState, tradeline }: SelectedTradeline) => {
    const tradelinesToConsolidate = modifiedTradelines.filter(
      (t) =>
        t.selectedForConsolidation &&
        (!tradelinesModifications[t.id] || tradelinesModifications[t.id].newState !== TradelineNewState.Unselected),
    );

    const tradelinesExcludingModified = tradelinesToConsolidate.filter((t) => t.id !== tradeline.id);

    const tradelinesToRebalance = [
      ...tradelinesExcludingModified,
      ...(newState === TradelineNewState.Selected ? [tradeline] : []),
    ];
    const rebalancedTradelines = setTradelinesForConsolidationAndCalculateBalance(tradelinesToRebalance, maxLoanAmount);

    const updatedModifications: SelectedTradelineState = { ...tradelinesModifications };

    // Replace or add the rebalanced tradelines to the modifications
    rebalancedTradelines.forEach((rt) => {
      updatedModifications[rt.id] = {
        tradeline: rt,
        newState: TradelineNewState.Selected,
      };
    });

    if (newState === TradelineNewState.Unselected) {
      if (originalTradelines.find((t) => t.id === tradeline.id)?.selectedForConsolidation) {
        updatedModifications[tradeline.id] = {
          tradeline,
          newState: TradelineNewState.Unselected,
        };
      } else {
        delete updatedModifications[tradeline.id];
      }
    }

    setSelectedTradelines(updatedModifications);
  };

  const resetModifications = () => setSelectedTradelines({});

  const updateTradelinePayoffAmount = (tradelineId: string, amount: number) => {
    const tradeline = originalTradelines.find((t) => t.id === tradelineId);
    if (!tradeline) return;

    const tradelineWithModifications: SelectedTradelineState = {
      ...tradelinesModifications,
      [tradeline.id]: {
        tradeline: { ...tradeline, balanceToConsolidate: amount },
        newState: TradelineNewState.Selected,
      },
    };

    setSelectedTradelines(tradelineWithModifications);
    return tradelineWithModifications;
  };

  return {
    tradelinesModifications,
    cantConsolidate,
    payOff,
    keepIt,
    tradelines: modifiedTradelines,
    toggleTradelineConsolidation,
    updateTradelinePayoffAmount,
    resetModifications,
  };
};

export default useDebtConsolidationState;
